import React, { useState, useEffect } from 'react';
import {
  Layout,
  Menu,
  Card,
  Button,
  Avatar,
  Typography,
  Statistic,
  Row,
  Col,
  Tag,

  Progress,
  Space,
  Badge,
  Table,
  Input,
  Form,
  Select,
  Divider,
  List,
  Empty,
  Skeleton,
  message,
  Popconfirm,
  Collapse,
  AutoComplete,
  Modal,
  Tabs,
  Alert
} from 'antd';
import {
  ApiOutlined,
  DiffOutlined,
  CloudUploadOutlined,
  SyncOutlined,
  CheckCircleOutlined,
  WarningOutlined,
  TeamOutlined,
  AppstoreOutlined,
  FileTextOutlined,
  BookOutlined,
  DashboardOutlined,
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  ReloadOutlined,
  ExclamationCircleOutlined,
  RiseOutlined,
  SafetyCertificateOutlined,
  ClockCircleOutlined,
  TagsOutlined,
  SortAscendingOutlined,
  FileAddOutlined,
  FolderOpenOutlined,
  RocketOutlined,
  HeartOutlined
} from '@ant-design/icons';
import ApiList from './ApiList';
import ApiConfigForm from './ApiConfigForm';
import RecipeForm from './RecipeForm';
import RecipeRunner from './RecipeRunner';
import { Editor } from '@tinymce/tinymce-react';
import apiClient from '../services/axiosConfig';
import { useTheme } from '../App';
import DiffChecker from './DiffChecker';
import IssuesSection from './IssuesSection';
import { BugOutlined } from '@ant-design/icons'; 

const { Header, Content, Sider } = Layout;
const { Title, Text, Paragraph } = Typography;
const { Search } = Input;
const { Option } = Select;
const { Panel } = Collapse;

// API Groups Section Component
const ApiGroupsSection = ({ refreshTrigger }) => {
  const { theme, currentTheme } = useTheme();
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isAdding, setIsAdding] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editingGroup, setEditingGroup] = useState(null);
  const [searchText, setSearchText] = useState('');
  const [form] = Form.useForm();
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;


  const fetchGroups = async () => {
    setLoading(true);
    try {
      const response = await apiClient.get(`${API_BASE_URL}/groups`, {
        headers: {
          'Cache-Control': 'no-cache',
          'Pragma': 'no-cache'
        }
      });
      setGroups(response.data);
    } catch (error) {
      message.error('Failed to fetch API groups');
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchGroups();
  }, []);

  // Refresh groups when refreshTrigger changes
  useEffect(() => {
    if (refreshTrigger > 0) {
      fetchGroups();
    }
  }, [refreshTrigger]);

  const handleDelete = async (groupId) => {
    try {
      await apiClient.delete(`${API_BASE_URL}/groups/${groupId}`);
      setGroups(prev => prev.filter(group => group._id !== groupId));
      message.success('Group deleted successfully');
    } catch (error) {
      message.error('Failed to delete group');
    }
  };

  const handleSubmit = async (values) => {
    try {
      if (isEditing && editingGroup) {
        const response = await apiClient.put(`${API_BASE_URL}/groups/${editingGroup._id}`, values);
        message.success('Group updated successfully');
        setGroups(prev =>
          prev.map(group => (group._id === editingGroup._id ? response.data.data : group))
        );
      } else {
        const response = await apiClient.post(`${API_BASE_URL}/groups`, values);
        message.success('Group created successfully');
        setGroups(prev => [...prev, response.data.data]);
      }
      form.resetFields();
      setIsAdding(false);
      setIsEditing(false);
      setEditingGroup(null);
    } catch (error) {
      message.error('Failed to save group');
    }
  };

  // Filter groups based on search text
  const filteredGroups = groups.filter(group =>
    !searchText ||
    group.name.toLowerCase().includes(searchText.toLowerCase()) ||
    (group.description && group.description.toLowerCase().includes(searchText.toLowerCase()))
  );

  const columns = [
    {
      title: 'No',
      render: (_, __, index) => index + 1,
      width: 60,
    },
    {
      title: 'Group Name',
      dataIndex: 'name',
      key: 'name',
      width: 150,
      render: (text) => <Text strong>{text}</Text>
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
      ellipsis: true,
    },
    {
      title: 'Sort Order',
      dataIndex: 'sortOrder',
      key: 'sortOrder',
      width: 120,
      sorter: (a, b) => a.sortOrder - b.sortOrder,
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 200,
      fixed: 'right',
      render: (_, record) => (
        <Space size="small">
          <Button 
            type="link" 
            icon={<EditOutlined />} 
            onClick={() => {
              setIsEditing(true);
              setEditingGroup(record);
              form.setFieldsValue(record);
            }}
            size="small"
          >
            Edit
          </Button>
          <Popconfirm
            title="Are you sure you want to delete this group?"
            onConfirm={() => handleDelete(record._id)}
            okText="Yes"
            cancelText="No"
          >
            <Button type="link" danger icon={<DeleteOutlined />} size="small">
              Delete
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: '24px' }}>
      <div style={{ marginBottom: '24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div></div>
        <Space>
          <Search
            placeholder="Search groups..."
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            allowClear
            style={{ width: 250 }}
          />
          <Button icon={<ReloadOutlined />} onClick={fetchGroups}>
            Refresh
          </Button>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => {
              setIsAdding(true);
              setIsEditing(false);
              form.resetFields();
            }}
          >
            New Group
          </Button>
        </Space>
      </div>

      {(isAdding || isEditing) && (
        <Card style={{ marginBottom: '24px', backgroundColor: theme.surface }}>
          <Form form={form} layout="vertical" onFinish={handleSubmit}>
            <Row gutter={16}>
              <Col span={8}>
                <Form.Item
                  name="name"
                  label="Group Name"
                  rules={[{ required: true, message: 'Please enter group name' }]}
                >
                  <Input placeholder="e.g., User Management" />
                </Form.Item>
              </Col>
              <Col span={4}>
                <Form.Item name="sortOrder" label="Sort Order">
                  <Input type="number" placeholder="0" />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item name="description" label="Description">
                  <Input.TextArea placeholder="Brief description of this API group" rows={1} />
                </Form.Item>
              </Col>
            </Row>
            <Space>
              <Button type="primary" htmlType="submit">
                {isEditing ? 'Update Group' : 'Create Group'}
              </Button>
              <Button onClick={() => {
                setIsAdding(false);
                setIsEditing(false);
                form.resetFields();
              }}>
                Cancel
              </Button>
            </Space>
          </Form>
        </Card>
      )}

      <Table 
        columns={columns} 
        dataSource={filteredGroups} 
        rowKey="_id" 
        loading={loading}
        scroll={{ x: 800 }}
        pagination={{ pageSize: 20, showSizeChanger: true }}
      />
    </div>
  );
};

const PublishingSection = () => {
    const { theme, currentTheme } = useTheme();
    const [publishStatus, setPublishStatus] = useState(null);
    const [loading, setLoading] = useState(false);
    const [publishing, setPublishing] = useState(false);
    const [syncingFromProd, setSyncingFromProd] = useState(false);
    const [selectedTab, setSelectedTab] = useState('overview');
    const [publishHistory, setPublishHistory] = useState([]);
    const [diffModalVisible, setDiffModalVisible] = useState(false); // Add this state
  
    const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
  
    const fetchPublishHistory = async () => {
      try {
        const response = await apiClient.get('/publish/history');
        setPublishHistory(response.data.data);
      } catch (error) {
        console.error('Failed to fetch publish history:', error);
      }
    };
    
    const fetchPublishStatus = async () => {
      setLoading(true);
      try {
        const response = await apiClient.get(`${API_BASE_URL}/publish/status`, {
          headers: { 'X-Dev-Mode': 'true' }
        });
        setPublishStatus(response.data);
      } catch (error) {
        message.error('Failed to fetch publish status');
      }
      setLoading(false);
    };
  
    useEffect(() => {
      fetchPublishStatus();
      fetchPublishHistory();
    }, []);
  
    const handlePublishAll = async () => {
      Modal.confirm({
        title: 'Publish All Changes to Production',
        content: (
          <div>
            <Alert
              message="Warning"
              description="This will replace ALL production data with current development data. This action cannot be undone."
              type="warning"
              showIcon
              style={{ marginBottom: 16 }}
            />
            <p>Are you absolutely sure you want to proceed?</p>
          </div>
        ),
        okText: 'Yes, Publish Everything',
        okType: 'danger',
        cancelText: 'Cancel',
        onOk: async () => {
          setPublishing(true);
          try {
            const response = await apiClient.post(`${API_BASE_URL}/publish/all`, {}, {
              headers: { 'X-Dev-Mode': 'true' }
            });
            message.success(`Successfully published ${response.data.stats.configs} APIs, ${response.data.stats.groups} groups, ${response.data.stats.docs} docs, and ${response.data.stats.recipes} recipes!`);
            fetchPublishStatus();
            fetchPublishHistory();
          } catch (error) {
            message.error('Failed to publish: ' + error.message);
          }
          setPublishing(false);
        }
      });
    };
  
    const handleSyncFromProd = async () => {
      Modal.confirm({
        title: 'Sync Development from Production',
        content: (
          <div>
            <Alert
              message="Warning"
              description="This will replace ALL development data with current production data. Any unpublished changes will be lost."
              type="warning"
              showIcon
              style={{ marginBottom: 16 }}
            />
            <p>This is useful for starting fresh or recovering from mistakes. Continue?</p>
          </div>
        ),
        okText: 'Yes, Sync from Production',
        okType: 'danger',
        cancelText: 'Cancel',
        onOk: async () => {
          setSyncingFromProd(true);
          try {
            const response = await apiClient.post(`${API_BASE_URL}/publish/sync-dev-from-prod`, {}, {
              headers: { 'X-Dev-Mode': 'true' }
            });
            message.success('Successfully synced development from production!');
            fetchPublishStatus();
          } catch (error) {
            message.error('Failed to sync: ' + error.message);
          }
          setSyncingFromProd(false);
        }
      });
    };
  
    if (loading) {
      return (
        <div style={{ padding: '24px' }}>
          <Skeleton active paragraph={{ rows: 4 }} />
        </div>
      );
    }
  
    return (
      <div style={{ padding: '24px' }}>
        {/* Header Actions */}
        <div style={{ marginBottom: '24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <Title level={4} style={{ margin: 0, color: theme.text }}>
              Publishing Management
            </Title>
            <Text type="secondary" style={{ fontSize: '14px' }}>
              Manage and deploy your development changes to production
            </Text>
          </div>
          <Space>
            <Button 
              icon={<DiffOutlined />} 
              onClick={() => setDiffModalVisible(true)}
              style={{
                borderColor: theme.primary,
                color: theme.primary
              }}
            >
              View Diff
            </Button>
            <Button 
              icon={<ReloadOutlined />} 
              onClick={fetchPublishStatus}
            >
              Refresh Status
            </Button>
            <Button
              icon={<SyncOutlined />}
              onClick={handleSyncFromProd}
              loading={syncingFromProd}
            >
              Sync from Production
            </Button>
            <Button
              type="primary"
              icon={<CloudUploadOutlined />}
              onClick={handlePublishAll}
              loading={publishing}
              style={{
                background: `linear-gradient(135deg, ${theme.primary} 0%, ${theme.primaryDark} 100%)`,
                border: 'none',
                boxShadow: `0 2px 8px ${theme.primary}40`,
              }}
            >
              Publish All Changes
            </Button>
          </Space>
        </div>
  
        {/* Status Overview */}
        {publishStatus && (
  <>
    <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
      <Col xs={24} sm={12} md={6}>
        <Card style={{ height: '100%', minHeight: '140px' }}> {/* Add fixed min-height */}
          <Statistic
            title="API Configurations"
            value={publishStatus.apiConfigs.dev}
            suffix={
              <span style={{ fontSize: '14px', color: theme.textSecondary }}>
                / {publishStatus.apiConfigs.prod} prod
              </span>
            }
            prefix={<ApiOutlined />}
            valueStyle={{ 
              color: publishStatus.apiConfigs.dev > publishStatus.apiConfigs.prod ? theme.accent : theme.success 
            }}
          />
          {publishStatus.apiConfigs.dev !== publishStatus.apiConfigs.prod && (
            <div style={{ marginTop: '8px' }}>
              <Tag color={publishStatus.apiConfigs.dev > publishStatus.apiConfigs.prod ? 'orange' : 'red'}>
                {Math.abs(publishStatus.apiConfigs.dev - publishStatus.apiConfigs.prod)} difference
              </Tag>
            </div>
          )}
        </Card>
      </Col>
      
      <Col xs={24} sm={12} md={6}>
        <Card style={{ height: '100%', minHeight: '140px' }}> {/* Add fixed min-height */}
          <Statistic
            title="API Groups"
            value={publishStatus.apiGroups.dev}
            suffix={
              <span style={{ fontSize: '14px', color: theme.textSecondary }}>
                / {publishStatus.apiGroups.prod} prod
              </span>
            }
            prefix={<AppstoreOutlined />}
            valueStyle={{ 
              color: publishStatus.apiGroups.dev > publishStatus.apiGroups.prod ? theme.accent : theme.success 
            }}
          />
          {publishStatus.apiGroups.dev !== publishStatus.apiGroups.prod && (
            <div style={{ marginTop: '8px' }}>
              <Tag color={publishStatus.apiGroups.dev > publishStatus.apiGroups.prod ? 'orange' : 'red'}>
                {Math.abs(publishStatus.apiGroups.dev - publishStatus.apiGroups.prod)} difference
              </Tag>
            </div>
          )}
        </Card>
      </Col>
      
      <Col xs={24} sm={12} md={6}>
        <Card style={{ height: '100%', minHeight: '140px' }}> {/* Add fixed min-height */}
          <Statistic
            title="Documentation"
            value={publishStatus.documentation.dev}
            suffix={
              <span style={{ fontSize: '14px', color: theme.textSecondary }}>
                / {publishStatus.documentation.prod} prod
              </span>
            }
            prefix={<FileTextOutlined />}
            valueStyle={{ 
              color: publishStatus.documentation.dev > publishStatus.documentation.prod ? theme.accent : theme.success 
            }}
          />
          {publishStatus.documentation.dev !== publishStatus.documentation.prod && (
            <div style={{ marginTop: '8px' }}>
              <Tag color={publishStatus.documentation.dev > publishStatus.documentation.prod ? 'orange' : 'red'}>
                {Math.abs(publishStatus.documentation.dev - publishStatus.documentation.prod)} difference
              </Tag>
            </div>
          )}
        </Card>
      </Col>
      
      <Col xs={24} sm={12} md={6}>
        <Card style={{ height: '100%', minHeight: '140px' }}> {/* Add fixed min-height */}
          <Statistic
            title="Recipes"
            value={publishStatus.recipes.dev}
            suffix={
              <span style={{ fontSize: '14px', color: theme.textSecondary }}>
                / {publishStatus.recipes.prod} prod
              </span>
            }
            prefix={<BookOutlined />}
            valueStyle={{ 
              color: publishStatus.recipes.dev > publishStatus.recipes.prod ? theme.accent : theme.success 
            }}
          />
          {publishStatus.recipes.dev !== publishStatus.recipes.prod && (
            <div style={{ marginTop: '8px' }}>
              <Tag color={publishStatus.recipes.dev > publishStatus.recipes.prod ? 'orange' : 'red'}>
                {Math.abs(publishStatus.recipes.dev - publishStatus.recipes.prod)} difference
              </Tag>
            </div>
          )}
        </Card>
      </Col>
    </Row>

    {/* If you want to add Validation Rules card in a new row */}
    <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
      <Col xs={24} sm={12} md={6}>
        <Card style={{ minHeight: '140px' }}>
          <Statistic
            title="Validation Rules"
            value={publishStatus.validationRules?.dev || 0}
            suffix={
              <span style={{ fontSize: '14px', color: theme.textSecondary }}>
                / {publishStatus.validationRules?.prod || 0} prod
              </span>
            }
            prefix={<SafetyCertificateOutlined />}
            valueStyle={{ 
              color: (publishStatus.validationRules?.dev || 0) > (publishStatus.validationRules?.prod || 0) ? theme.accent : theme.success 
            }}
          />
          {publishStatus.validationRules && publishStatus.validationRules.dev !== publishStatus.validationRules.prod && (
            <div style={{ marginTop: '8px' }}>
              <Tag color={(publishStatus.validationRules.dev || 0) > (publishStatus.validationRules.prod || 0) ? 'orange' : 'red'}>
                {Math.abs((publishStatus.validationRules.dev || 0) - (publishStatus.validationRules.prod || 0))} difference
              </Tag>
            </div>
          )}
        </Card>
      </Col>
            </Row>
  
            {/* Quick Diff Summary Card */}
            {(publishStatus.apiConfigs.dev !== publishStatus.apiConfigs.prod ||
              publishStatus.apiGroups.dev !== publishStatus.apiGroups.prod ||
              publishStatus.documentation.dev !== publishStatus.documentation.prod ||
              publishStatus.recipes.dev !== publishStatus.recipes.prod) && (
              <Card style={{ marginBottom: '24px' }}>
                <Alert
                  message="Changes Detected"
                  description={
                    <Space direction="vertical">
                      <Text>There are differences between your development and production environments.</Text>
                      <Button 
                        type="primary" 
                        ghost 
                        icon={<DiffOutlined />}
                        onClick={() => setDiffModalVisible(true)}
                      >
                        View Detailed Differences
                      </Button>
                    </Space>
                  }
                  type="info"
                  showIcon
                />
              </Card>
            )}
  
            {/* Detailed Information */}
            <Card>
              <Tabs activeKey={selectedTab} onChange={setSelectedTab}>
                <Tabs.TabPane tab="Overview" key="overview">
                  <Alert
                    message="Publishing Process"
                    description={
                      <div>
                        <p>The publishing process will:</p>
                        <ul>
                          <li>Replace all production APIs, Groups, Documentation, and Recipes with development versions</li>
                          <li>Update all references and maintain data integrity</li>
                          <li>This process is immediate and cannot be undone</li>
                        </ul>
                      </div>
                    }
                    type="info"
                    showIcon
                    style={{ marginBottom: 16 }}
                  />
                  
                  <Divider />
                  
                  <Title level={5} style={{ color: theme.text }}>Environment Status</Title>
                  <Row gutter={[16, 16]}>
                    <Col span={12}>
                      <Card style={{ backgroundColor: theme.surfaceLight }}>
                        <Space>
                          <Badge status="processing" />
                          <Text strong>Development Environment</Text>
                        </Space>
                        <div style={{ marginTop: 12 }}>
                          <Text type="secondary">You are currently working here</Text>
                        </div>
                      </Card>
                    </Col>
                    <Col span={12}>
                      <Card style={{ backgroundColor: theme.surfaceLight }}>
                        <Space>
                          <Badge status="success" />
                          <Text strong>Production Environment</Text>
                        </Space>
                        <div style={{ marginTop: 12 }}>
                          <Text type="secondary">Live environment for users</Text>
                        </div>
                      </Card>
                    </Col>
                  </Row>
                </Tabs.TabPane>
                
                <Tabs.TabPane tab="Guidelines" key="guidelines">
                  <Title level={5} style={{ color: theme.text }}>Publishing Best Practices</Title>
                  <List
                    size="small"
                    dataSource={[
                      {
                        title: 'Test Thoroughly',
                        description: 'Ensure all APIs are tested in development before publishing',
                        icon: <CheckCircleOutlined style={{ color: theme.success }} />
                      },
                      {
                        title: 'Document Changes',
                        description: 'Make sure all new APIs have proper documentation and examples',
                        icon: <FileTextOutlined style={{ color: theme.primary }} />
                      },
                      {
                        title: 'Review Differences',
                        description: 'Use the diff viewer to understand what will change in production',
                        icon: <DiffOutlined style={{ color: theme.accent }} />
                      },
                      {
                        title: 'Review Health Issues',
                        description: 'Check the overview dashboard for any missing fields or documentation',
                        icon: <HeartOutlined style={{ color: theme.error }} />
                      },
                      {
                        title: 'Coordinate with Team',
                        description: 'Inform your team before publishing major changes',
                        icon: <TeamOutlined style={{ color: theme.accent }} />
                      },
                      {
                        title: 'Backup Important Data',
                        description: 'Consider exporting critical configurations before major updates',
                        icon: <CloudUploadOutlined style={{ color: theme.primary }} />
                      }
                    ]}
                    renderItem={item => (
                      <List.Item>
                        <List.Item.Meta
                          avatar={item.icon}
                          title={<Text strong>{item.title}</Text>}
                          description={<Text type="secondary">{item.description}</Text>}
                        />
                      </List.Item>
                    )}
                  />
                </Tabs.TabPane>
                
                <Tabs.TabPane tab="History" key="history">
                  {publishHistory.length > 0 ? (
                    <Table
                      dataSource={publishHistory}
                      rowKey="_id"
                      pagination={{ pageSize: 10 }}
                      columns={[
                        {
                          title: 'Date',
                          dataIndex: 'publishedAt',
                          key: 'publishedAt',
                          render: (date) => new Date(date).toLocaleString(),
                          width: 180
                        },
                        {
                          title: 'Action',
                          dataIndex: 'action',
                          key: 'action',
                          render: (action) => (
                            <Tag color={action === 'publish_all' ? 'blue' : 'green'}>
                              {action === 'publish_all' ? 'Published to Production' : 'Synced from Production'}
                            </Tag>
                          )
                        },
                        {
                          title: 'Items Published',
                          key: 'stats',
                          render: (_, record) => (
                            <Space size="small">
                              <Tag>APIs: {record.stats.apiConfigs}</Tag>
                              <Tag>Groups: {record.stats.apiGroups}</Tag>
                              <Tag>Docs: {record.stats.documentation}</Tag>
                              <Tag>Recipes: {record.stats.recipes}</Tag>
                              <Tag>Rules: {record.stats.validationRules || 0}</Tag>
                            </Space>
                          )
                        },
                        {
                          title: 'Duration',
                          dataIndex: 'duration',
                          key: 'duration',
                          render: (ms) => `${(ms / 1000).toFixed(2)}s`,
                          width: 100
                        },
                        {
                          title: 'Status',
                          dataIndex: 'status',
                          key: 'status',
                          render: (status) => (
                            <Tag color={status === 'success' ? 'green' : 'red'}>
                              {status.toUpperCase()}
                            </Tag>
                          ),
                          width: 100
                        }
                      ]}
                    />
                  ) : (
                    <Empty description="No publishing history yet" />
                  )}
                </Tabs.TabPane>
              </Tabs>
            </Card>
          </>
        )}
  
        {/* Diff Checker Modal */}
        <DiffChecker
          visible={diffModalVisible}
          onClose={() => setDiffModalVisible(false)}
        />
      </div>
    );
  };
// Documentation Section Component
const DocumentationSection = () => {
  const { theme, currentTheme } = useTheme();
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isAdding, setIsAdding] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editingDoc, setEditingDoc] = useState(null);
  const [form] = Form.useForm();
  const [categories, setCategories] = useState([]);
  const [editorContent, setEditorContent] = useState('');
  const [searchText, setSearchText] = useState('');
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

  const fetchDocs = async () => {
    setLoading(true);
    try {
      const response = await apiClient.get(`${API_BASE_URL}/documentation`);
      setDocs(response.data);
    } catch (error) {
      message.error('Failed to fetch documentation');
    }
    setLoading(false);
  };

  const fetchCategories = async () => {
    try {
      const response = await apiClient.get(`${API_BASE_URL}/documentation/categories/distinct`);
      setCategories(response.data);
    } catch (error) {
      message.error('Failed to fetch categories');
    }
  };

  useEffect(() => {
    fetchDocs();
    fetchCategories();
  }, []);

  const handleDelete = async (id) => {
    try {
      await apiClient.delete(`${API_BASE_URL}/documentation/${id}`);
      setDocs(prev => prev.filter(doc => doc._id !== id));
      message.success('Documentation deleted');
    } catch (error) {
      message.error('Failed to delete documentation');
    }
  };

  const handleSubmit = async (values) => {
    values.content = editorContent;
    try {
      if (isEditing && editingDoc) {
        await apiClient.put(`${API_BASE_URL}/documentation/${editingDoc._id}`, values);
        message.success('Documentation updated');
      } else {
        await apiClient.post(`${API_BASE_URL}/documentation`, values);
        message.success('Documentation created');
      }
      fetchDocs();
      form.resetFields();
      setIsAdding(false);
      setIsEditing(false);
      setEditingDoc(null);
      setEditorContent('');
    } catch (error) {
      message.error('Failed to save documentation');
    }
  };

  const filteredDocs = docs.filter(doc => 
    !searchText || 
    doc.title.toLowerCase().includes(searchText.toLowerCase()) ||
    doc.category.toLowerCase().includes(searchText.toLowerCase()) ||
    (doc.tags && doc.tags.some(tag => tag.toLowerCase().includes(searchText.toLowerCase())))
  );

  const columns = [
    {
      title: 'Title',
      dataIndex: 'title',
      key: 'title',
      render: (text) => <Text strong>{text}</Text>,
    },
    {
      title: 'Category',
      dataIndex: 'category',
      key: 'category',
      filters: categories.map(cat => ({ text: cat, value: cat })),
      onFilter: (value, record) => record.category === value,
    },
    {
      title: 'Tags',
      dataIndex: 'tags',
      key: 'tags',
      render: tags => (
        <>
          {tags?.map(tag => (
            <Tag color="blue" key={tag} style={{ marginBottom: '4px' }}>
              {tag}
            </Tag>
          ))}
        </>
      ),
    },
    {
      title: 'Sort Order',
      dataIndex: 'sortOrder',
      key: 'sortOrder',
      width: 100,
      sorter: (a, b) => a.sortOrder - b.sortOrder,
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 150,
      render: (_, record) => (
        <Space size="small">
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => {
              setIsEditing(true);
              setEditingDoc(record);
              form.setFieldsValue(record);
              setEditorContent(record.content);
            }}
          >
            Edit
          </Button>
          <Popconfirm
            title="Delete this documentation?"
            onConfirm={() => handleDelete(record._id)}
          >
            <Button type="link" danger icon={<DeleteOutlined />}>
              Delete
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: '24px' }}>
      <div style={{ marginBottom: '24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div></div>
        <Space>
          <Search
            placeholder="Search documentation..."
            onChange={(e) => setSearchText(e.target.value)}
            style={{ width: 250 }}
          />
          <Button icon={<ReloadOutlined />} onClick={fetchDocs}>
            Refresh
          </Button>
          <Button
            type="primary"
            icon={<FileAddOutlined />}
            onClick={() => {
              setIsAdding(true);
              setIsEditing(false);
              form.resetFields();
              setEditorContent('');
            }}
          >
            Add Documentation
          </Button>
        </Space>
      </div>

      {(isAdding || isEditing) && (
        <Card style={{ marginBottom: '24px', backgroundColor: theme.surface }}>
          <Form form={form} layout="vertical" onFinish={handleSubmit}>
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  name="title"
                  label="Title"
                  rules={[{ required: true, message: 'Please enter a title' }]}
                >
                  <Input placeholder="Documentation title" />
                </Form.Item>
              </Col>
              <Col span={6}>
                <Form.Item
                  name="category"
                  label="Category"
                  rules={[{ required: true, message: 'Please select or enter a category' }]}
                >
                  <AutoComplete
                    placeholder="Select or create category"
                    options={categories.map(cat => ({ value: cat }))}
                  />
                </Form.Item>
              </Col>
              <Col span={6}>
                <Form.Item
                  name="sortOrder"
                  label="Sort Order"
                  rules={[{ required: true, message: 'Please enter sort order' }]}
                >
                  <Input type="number" placeholder="1" />
                </Form.Item>
              </Col>
            </Row>
            
            <Form.Item name="tags" label="Tags">
              <Select mode="tags" placeholder="Add tags" />
            </Form.Item>
            
            <Form.Item label="Content" required>
              <Editor
                apiKey="0k7t1yanut3hbo2kq78m4rik6iy2tc50gut6gsh3q2g7wed2"
                init={{
                  height: 400,
                  menubar: true,
                  plugins: ['table', 'lists', 'link', 'image', 'code', 'codesample'],
                  toolbar: 'undo redo | formatselect | bold italic underline | alignleft aligncenter alignright alignjustify | bullist numlist outdent indent | table codesample | code',
                  skin: currentTheme === 'dark' ? 'oxide-dark' : 'oxide',
                  content_css: currentTheme === 'dark' ? 'dark' : 'default',
                }}
                onEditorChange={(content) => setEditorContent(content)}
                value={editorContent}
              />
            </Form.Item>
            
            <Space>
              <Button type="primary" htmlType="submit">
                {isEditing ? 'Update Documentation' : 'Add Documentation'}
              </Button>
              <Button onClick={() => {
                setIsAdding(false);
                setIsEditing(false);
                form.resetFields();
                setEditorContent('');
              }}>
                Cancel
              </Button>
            </Space>
          </Form>
        </Card>
      )}

      <Table
        columns={columns}
        dataSource={filteredDocs}
        rowKey="_id"
        loading={loading}
        pagination={{ pageSize: 20, showSizeChanger: true }}
      />
    </div>
  );
};
// In ApiManager.js, update the RecipesSection component:
const RecipesSection = () => {
  const { theme } = useTheme();
  const [recipes, setRecipes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isAdding, setIsAdding] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editingRecipe, setEditingRecipe] = useState(null);
  const [searchText, setSearchText] = useState('');
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

  const fetchRecipes = async () => {
    setLoading(true);
    try {
      const response = await apiClient.get(`${API_BASE_URL}/recipes`);
      setRecipes(response.data);
    } catch (error) {
      message.error('Failed to fetch recipes');
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchRecipes();
  }, []);

  const handleDelete = async (id) => {
    try {
      await apiClient.delete(`${API_BASE_URL}/recipes/${id}`);
      setRecipes(prev => prev.filter(recipe => recipe._id !== id));
      message.success('Recipe deleted successfully');
    } catch (error) {
      message.error('Failed to delete recipe');
    }
  };

  const handleRecipeSubmit = async (values) => {
    try {
      if (isEditing && editingRecipe) {
        await apiClient.put(`${API_BASE_URL}/recipes/${editingRecipe._id}`, values);
        message.success('Recipe updated successfully');
      } else {
        await apiClient.post(`${API_BASE_URL}/recipes`, values);
        message.success('Recipe created successfully');
      }
      fetchRecipes();
      setIsAdding(false);
      setIsEditing(false);
      setEditingRecipe(null);
    } catch (error) {
      message.error('Failed to save recipe');
    }
  };

  const filteredRecipes = recipes.filter(recipe =>
    !searchText ||
    recipe.name.toLowerCase().includes(searchText.toLowerCase()) ||
    recipe.description?.toLowerCase().includes(searchText.toLowerCase()) ||
    recipe.tags?.some(tag => tag.toLowerCase().includes(searchText.toLowerCase()))
  );

  if (isAdding || isEditing) {
    return (
      <RecipeForm
        initialValues={editingRecipe}
        isEditMode={isEditing}
        onSubmit={handleRecipeSubmit}
        onCancel={() => {
          setIsAdding(false);
          setIsEditing(false);
          setEditingRecipe(null);
        }}
      />
    );
  }

  return (
    <div style={{ padding: '24px' }}>
      <div style={{ marginBottom: '24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div></div>
        <Space>
          <Search
            placeholder="Search recipes..."
            onChange={(e) => setSearchText(e.target.value)}
            style={{ width: 250 }}
          />
          <Button icon={<ReloadOutlined />} onClick={fetchRecipes}>
            Refresh
          </Button>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => setIsAdding(true)}
          >
            Create Recipe
          </Button>
        </Space>
      </div>

      {loading ? (
        <Skeleton active paragraph={{ rows: 4 }} />
      ) : filteredRecipes.length === 0 ? (
        <Empty
          description="No recipes found"
          image={Empty.PRESENTED_IMAGE_SIMPLE}
        >
          <Button type="primary" onClick={() => setIsAdding(true)}>
            Create Your First Recipe
          </Button>
        </Empty>
      ) : (
        <Table
          columns={[
            {
              title: 'Name',
              dataIndex: 'name',
              key: 'name',
              width: '25%',
              render: (text) => <Text strong>{text}</Text>,
            },
            {
              title: 'Description',
              dataIndex: 'description',
              key: 'description',
              width: '35%',
              ellipsis: true,
              render: (text) => text || '-',
            },
            {
              title: 'Steps',
              dataIndex: 'steps',
              key: 'steps',
              width: '10%',
              align: 'center',
              render: (steps) => <Tag color="blue">{steps?.length || 0} steps</Tag>,
            },
            {
              title: 'Tags',
              dataIndex: 'tags',
              key: 'tags',
              width: '15%',
              render: (tags) => (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                  {tags?.length > 0 ? tags.map(tag => (
                    <Tag key={tag} color="purple">
                      {tag}
                    </Tag>
                  )) : '-'}
                </div>
              ),
            },
            {
              title: 'Actions',
              key: 'actions',
              width: '15%',
              fixed: 'right',
              render: (_, record) => (
                <Space size="small">
                  <Button
                    type="link"
                    icon={<EditOutlined />}
                    onClick={() => {
                      setEditingRecipe(record);
                      setIsEditing(true);
                    }}
                    style={{ padding: '4px 8px' }}
                  >
                    Edit
                  </Button>
                  <Popconfirm
                    title="Delete this recipe?"
                    onConfirm={() => handleDelete(record._id)}
                  >
                    <Button 
                      type="link" 
                      danger 
                      icon={<DeleteOutlined />}
                      style={{ padding: '4px 8px' }}
                    >
                      Delete
                    </Button>
                  </Popconfirm>
                </Space>
              ),
            },
          ]}
          dataSource={filteredRecipes}
          rowKey="_id"
          loading={loading}
          pagination={{ 
            pageSize: 20, 
            showSizeChanger: true,
            showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
          }}
          scroll={{ x: 'max-content' }}
        />
      )}
    </div>
  );
};



// Overview Dashboard Component
const OverviewDashboard = ({ stats, onNavigate, onEditApi, isLoadingStats }) => {
  const { theme, currentTheme } = useTheme();
  const [healthModalVisible, setHealthModalVisible] = useState(false);
  const [selectedHealthIssue, setSelectedHealthIssue] = useState(null);
  const [issueApis, setIssueApis] = useState([]);
  const [groupsPage, setGroupsPage] = useState(0);
  const groupsPerPage = 5;

  const showHealthIssues = (issueType, apis) => {
    setSelectedHealthIssue(issueType);
    setIssueApis(apis);
    setHealthModalVisible(true);
  };

  // Get paginated groups
  const allGroups = Object.entries(stats.groupDistribution || {})
    .sort(([,a], [,b]) => b - a)
    .map(([group, count]) => ({ group, count }));
  
  const totalPages = Math.ceil(allGroups.length / groupsPerPage);
  const currentGroups = allGroups.slice(
    groupsPage * groupsPerPage, 
    (groupsPage + 1) * groupsPerPage
  );

  if (isLoadingStats) {
    return (
      <div style={{ padding: '24px' }}>
        <Skeleton active paragraph={{ rows: 4 }} />
        <Skeleton active paragraph={{ rows: 4 }} />
      </div>
    );
  }

  return (
    <div style={{ padding: '24px' }}>
      {/* Key Metrics */}
      <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
        <Col xs={24} sm={12} md={6}>
          <Card 
            hoverable 
            onClick={() => onNavigate('apis')}
            style={{ cursor: 'pointer', height: '100%' }}
          >
            <Statistic
              title="Total APIs"
              value={stats.totalApis}
              prefix={<ApiOutlined />}
              valueStyle={{ color: theme.primary }}
            />
            <div style={{ marginTop: '16px' }}>
              <Text type="secondary">
                <RiseOutlined style={{ color: theme.success }} /> Active and configured
              </Text>
            </div>
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card 
            hoverable 
            onClick={() => onNavigate('groups')}
            style={{ cursor: 'pointer', height: '100%' }}
          >
            <Statistic
              title="Groups"
              value={stats.totalGroups}
              prefix={<AppstoreOutlined />}
              valueStyle={{ color: '#722ed1' }}
            />
            <div style={{ marginTop: '16px' }}>
              <Text type="secondary">
                <FolderOpenOutlined style={{ color: theme.accent }} /> Organized collections
              </Text>
            </div>
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card 
            hoverable 
            onClick={() => onNavigate('docs')}
            style={{ cursor: 'pointer', height: '100%' }}
          >
            <Statistic
              title="Documentation"
              value={stats.totalDocs}
              prefix={<FileTextOutlined />}
              valueStyle={{ color: '#13c2c2' }}
            />
            <div style={{ marginTop: '16px' }}>
              <Text type="secondary">
                <BookOutlined style={{ color: theme.primary }} /> Knowledge base
              </Text>
            </div>
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card 
            hoverable 
            onClick={() => onNavigate('recipes')}
            style={{ cursor: 'pointer', height: '100%' }}
          >
            <Statistic
              title="Recipes"
              value={stats.totalRecipes}
              prefix={<BookOutlined />}
              valueStyle={{ color: '#fa541c' }}
            />
            <div style={{ marginTop: '16px' }}>
              <Text type="secondary">
                <RocketOutlined style={{ color: theme.error }} /> Workflow automation
              </Text>
            </div>
          </Card>
        </Col>
      </Row>

      {/* Second row for Issues card */}
      <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
        <Col xs={24} sm={12} md={6}>
          <Card 
            hoverable 
            onClick={() => onNavigate('issues')}
            style={{ cursor: 'pointer', height: '100%' }}
          >
            <Statistic
              title="Open Issues"
              value={stats.openIssues}
              suffix={
                <span style={{ fontSize: '14px', color: theme.textSecondary }}>
                  / {stats.totalIssues} total
                </span>
              }
              prefix={<BugOutlined />}
              valueStyle={{ color: stats.openIssues > 0 ? '#ff4d4f' : '#52c41a' }}
            />
            <div style={{ marginTop: '16px' }}>
              <Text type="secondary">
                {stats.openIssues > 0 ? (
                  <>
                    <ExclamationCircleOutlined style={{ color: theme.error }} /> Needs attention
                  </>
                ) : (
                  <>
                    <CheckCircleOutlined style={{ color: theme.success }} /> All resolved
                  </>
                )}
              </Text>
            </div>
          </Card>
        </Col>
      </Row>

      {/* API Health Checklist */}
      <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
        <Col xs={24} lg={12}>
          <Card title={
            <span>
              <HeartOutlined style={{ marginRight: '8px', color: theme.primary }} />
              Health Checklist
            </span>
          }>
            <List
              size="small"
              dataSource={[
                {
                  title: 'Missing examples',
                  count: stats.healthIssues?.missingExamples || 0,
                  color: '#ff4d4f',
                  type: 'missingExamples',
                  apis: stats.healthIssueApis?.missingExamples || []
                },
                {
                  title: 'Missing long description',
                  count: stats.healthIssues?.missingLongDescription || 0,
                  color: '#faad14',
                  type: 'missingLongDescription',
                  apis: stats.healthIssueApis?.missingLongDescription || []
                },
                {
                  title: 'Missing tags',
                  count: stats.healthIssues?.missingTags || 0,
                  color: '#1890ff',
                  type: 'missingTags',
                  apis: stats.healthIssueApis?.missingTags || []
                },
                {
                  title: 'Missing description',
                  count: stats.healthIssues?.missingDescription || 0,
                  color: '#722ed1',
                  type: 'missingDescription',
                  apis: stats.healthIssueApis?.missingDescription || []
                },
                {
                  title: 'Reported issues',
                  count: stats.openIssues || 0,
                  color: '#ff4d4f',
                  type: 'reportedIssues',
                  apis: [] // No direct API list for reported issues
                }
              ]}
              renderItem={item => (
                <List.Item
                  style={{ 
                    cursor: item.count > 0 ? 'pointer' : 'default',
                    transition: 'background-color 0.2s',
                    padding: '8px 12px',
                    borderRadius: '6px'
                  }}
                  onClick={item.count > 0 ? () => {
                    if (item.type === 'reportedIssues') {
                      onNavigate('issues');
                    } else {
                      showHealthIssues(item.type, item.apis);
                    }
                  } : undefined}
                  onMouseEnter={(e) => {
                    if (item.count > 0) {
                      e.currentTarget.style.backgroundColor = theme.surface;
                    }
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'transparent';
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%', alignItems: 'center' }}>
                    <Text type={item.count > 0 ? 'default' : 'secondary'}>{item.title}</Text>
                    <Tag color={item.count > 0 ? item.color : 'green'}>
                      {item.count > 0 ? `${item.count} issues` : 'All good ✓'}
                    </Tag>
                  </div>
                </List.Item>
              )}
            />
          </Card>
        </Col>

        {/* Top API Groups */}
        <Col xs={24} lg={12}>
          <Card title={
            <span>
              <AppstoreOutlined style={{ marginRight: '8px', color: theme.primary }} />
              Groups Distribution
            </span>
          }>
            <List
              size="small"
              dataSource={currentGroups}
              renderItem={item => (
                <List.Item>
                  <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%', alignItems: 'center' }}>
                    <Text strong>{item.group}</Text>
                    <Tag color="purple">{item.count} APIs</Tag>
                  </div>
                </List.Item>
              )}
              locale={{ emptyText: 'No API groups found' }}
            />
            {totalPages > 1 && (
              <div style={{ 
                marginTop: '12px', 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center',
                padding: '0 8px'
              }}>
                <Button
                  size="small"
                  onClick={() => setGroupsPage(prev => prev - 1)}
                  disabled={groupsPage === 0}
                  style={{ 
                    border: 'none',
                    boxShadow: 'none',
                    color: theme.textSecondary
                  }}
                >
                  ← Previous
                </Button>
                <Text style={{ 
                  fontSize: '12px', 
                  color: theme.textSecondary,
                  fontWeight: '500'
                }}>
                  {groupsPage + 1} of {totalPages}
                </Text>
                <Button
                  size="small"
                  onClick={() => setGroupsPage(prev => prev + 1)}
                  disabled={groupsPage === totalPages - 1}
                  style={{ 
                    border: 'none',
                    boxShadow: 'none',
                    color: theme.textSecondary
                  }}
                >
                  Next →
                </Button>
              </div>
            )}
          </Card>
        </Col>
      </Row>

      {/* Recent Activity */}
      <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
        <Col xs={24} lg={8}>
          <Card title={
            <span>
              <ClockCircleOutlined style={{ marginRight: '8px', color: theme.primary }} />
              Recent APIs
            </span>
          } size="small">
            <List
              size="small"
              dataSource={stats.recentApis || []}
              renderItem={item => (
                <List.Item>
                  <div>
                    <Text strong>{item.name}</Text>
                    <br />
                    <Text type="secondary" style={{ fontSize: '12px' }}>
                      {item.group} • {item.method}
                    </Text>
                  </div>
                </List.Item>
              )}
              locale={{ emptyText: 'No APIs yet' }}
            />
          </Card>
        </Col>

        <Col xs={24} lg={8}>
          <Card title={
            <span>
              <FileTextOutlined style={{ marginRight: '8px', color: theme.primary }} />
              Recent Documentation
            </span>
          } size="small">
            <List
              size="small"
              dataSource={stats.recentDocs || []}
              renderItem={item => (
                <List.Item>
                  <div>
                    <Text strong>{item.title}</Text>
                    <br />
                    <Text type="secondary" style={{ fontSize: '12px' }}>
                      {item.category}
                    </Text>
                  </div>
                </List.Item>
              )}
              locale={{ emptyText: 'No documentation yet' }}
            />
          </Card>
        </Col>

        <Col xs={24} lg={8}>
          <Card title={
            <span>
              <RocketOutlined style={{ marginRight: '8px', color: theme.primary }} />
              Quick Actions
            </span>
          } size="small">
            <Space direction="vertical" style={{ width: '100%' }}>
              <Button 
                type="primary" 
                icon={<PlusOutlined />} 
                onClick={() => onNavigate('apis', { action: 'add' })}
                block
              >
                Add API
              </Button>
              <Button 
                icon={<AppstoreOutlined />} 
                onClick={() => onNavigate('groups')}
                block
              >
                Groups
              </Button>
              <Button 
                icon={<FileAddOutlined />} 
                onClick={() => onNavigate('docs')}
                block
              >
                Add Docs
              </Button>
              <Button 
                icon={<BookOutlined />} 
                onClick={() => onNavigate('recipes')}
                block
              >
                Recipes
              </Button>
              <Button 
                icon={<BugOutlined />} 
                onClick={() => onNavigate('issues')}
                block
                danger={stats.openIssues > 0} // Make it red if there are open issues
              >
                View Issues {stats.openIssues > 0 && `(${stats.openIssues})`}
              </Button>
            </Space>
          </Card>
        </Col>
      </Row>

      {/* Health Issues Modal */}
      <Modal
        title={
          <span>
            <ClockCircleOutlined style={{ marginRight: '8px', color: theme.primary }} />
            {selectedHealthIssue === 'missingExamples' && 'Missing Examples'}
            {selectedHealthIssue === 'missingLongDescription' && 'Missing Long Description'}
            {selectedHealthIssue === 'missingTags' && 'Missing Tags'}
            {selectedHealthIssue === 'missingDescription' && 'Missing Description'}
          </span>
        }
        open={healthModalVisible}
        onCancel={() => setHealthModalVisible(false)}
        footer={[
          <Button key="close" onClick={() => setHealthModalVisible(false)}>
            Close
          </Button>,
          <Button 
            key="fix" 
            type="primary" 
            onClick={() => {
              setHealthModalVisible(false);
              onNavigate('apis');
            }}
          >
            Go to APIs
          </Button>
        ]}
        width={800}
      >
        <div style={{ marginBottom: '16px' }}>
          <Text type="secondary">
            Found {issueApis.length} APIs that need attention. Click on any API to edit it directly.
          </Text>
        </div>
        
        <Table
          size="small"
          dataSource={issueApis}
          rowKey="_id"
          pagination={{ pageSize: 10 }}
          columns={[
            {
              title: 'API Name',
              dataIndex: 'name',
              key: 'name',
              render: (text, record) => (
                <Button 
                  type="link" 
                  style={{ padding: 0, height: 'auto', fontWeight: 500 }}
                  onClick={async () => {
                    setHealthModalVisible(false);
                    // Directly edit this API
                    if (onEditApi) {
                      onEditApi(record);
                    }
                  }}
                >
                  {text}
                </Button>
              )
            },
            {
              title: 'Group',
              dataIndex: 'group',
              key: 'group',
              width: 120,
              render: (group) => <Tag color="purple">{group}</Tag>
            },
            {
              title: 'Route',
              dataIndex: 'route',
              key: 'route',
              width: 200,
              ellipsis: true,
              render: (route) => (
                <code style={{ 
                  fontSize: '12px', 
                  backgroundColor: theme.codeBg || '#f6f8fa',
                  padding: '2px 6px',
                  borderRadius: '3px',
                  color: theme.primary
                }}>
                  {route}
                </code>
              )
            },
            {
              title: 'Method',
              dataIndex: 'method',
              key: 'method',
              width: 80,
              render: (method) => (
                <Tag 
                  color={
                    method === 'GET' ? 'green' :
                    method === 'POST' ? 'blue' :
                    method === 'PUT' ? 'orange' :
                    method === 'DELETE' ? 'red' :
                    'default'
                  }
                >
                  {method}
                </Tag>
              )
            },
            {
              title: 'Issue',
              key: 'issue',
              width: 150,
              render: () => (
                <Tag color="red">
                  {selectedHealthIssue === 'missingExamples' && 'No Examples'}
                  {selectedHealthIssue === 'missingLongDescription' && 'No Long Description'}
                  {selectedHealthIssue === 'missingTags' && 'No Tags'}
                  {selectedHealthIssue === 'missingDescription' && 'No Description'}
                </Tag>
              )
            }
          ]}
        />
      </Modal>
    </div>
  );
};


// Main API Manager Component
const ApiManager = ({ onCancel, onApiChange }) => {
  const { theme, currentTheme } = useTheme();
  const [activeSection, setActiveSection] = useState('overview');
  const [collapsed, setCollapsed] = useState(false);
  const [isLoadingStats, setIsLoadingStats] = useState(true);
  const [stats, setStats] = useState({
    totalApis: 0,
    totalGroups: 0,
    totalDocs: 0,
    totalRecipes: 0,
    healthIssues: {},
    healthIssueApis: {},
    groupDistribution: {},
    recentApis: [],
    recentDocs: [],
    recentGroups: []
  });
  
  // State for managing forms
  const [isAddingApi, setIsAddingApi] = useState(false);
  const [isEditingApi, setIsEditingApi] = useState(false);
  const [editingApiData, setEditingApiData] = useState(null);
  const [refreshTrigger, setRefreshTrigger] = useState(0); // Add refresh trigger

  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

// Update fetchStats function to include issues:
const fetchStats = async () => {
  setIsLoadingStats(true);
  try {
    const [apisRes, groupsRes, docsRes, recipesRes, issuesRes] = await Promise.all([
      apiClient.get(`${API_BASE_URL}/config`, {
        params: {
          pageSize: 1000,
          page: 1,
          includeDetails: true
        },
        headers: {
          'Cache-Control': 'no-cache',
          'Pragma': 'no-cache'
        }
      }),
      apiClient.get(`${API_BASE_URL}/groups`, {
        headers: {
          'Cache-Control': 'no-cache',
          'Pragma': 'no-cache'
        }
      }),
      apiClient.get(`${API_BASE_URL}/documentation`, {
        headers: {
          'Cache-Control': 'no-cache',
          'Pragma': 'no-cache'
        }
      }),
      apiClient.get(`${API_BASE_URL}/recipes`, {
        headers: {
          'Cache-Control': 'no-cache',
          'Pragma': 'no-cache'
        }
      }),
      // Add issues stats fetch
      apiClient.get(`${API_BASE_URL}/issues/stats`)
    ]);

    const apis = apisRes.data.data || apisRes.data || [];
    const groups = groupsRes.data || [];
    const docs = docsRes.data || [];
    const recipes = recipesRes.data || [];
    const issuesStats = issuesRes.data.data || {};

    // Calculate API health issues
    const healthIssues = {
      missingExamples: apis.filter(api => !api.examples || api.examples.length === 0).length,
      missingLongDescription: apis.filter(api => !api.longDescription || api.longDescription.trim() === '').length,
      missingTags: apis.filter(api => !api.tags || api.tags.length === 0).length,
      missingDescription: apis.filter(api => !api.description || api.description.trim() === '').length,
      reportedIssues: issuesStats.openIssues || 0 // Add reported issues count
    };

    // Store actual APIs with issues for detailed view
    const healthIssueApis = {
      missingExamples: apis.filter(api => !api.examples || api.examples.length === 0),
      missingLongDescription: apis.filter(api => !api.longDescription || api.longDescription.trim() === ''),
      missingTags: apis.filter(api => !api.tags || api.tags.length === 0),
      missingDescription: apis.filter(api => !api.description || api.description.trim() === ''),
    };

    // Group APIs by group for distribution
    const groupDistribution = {};
    apis.forEach(api => {
      const groupName = api.group || 'Ungrouped';
      groupDistribution[groupName] = (groupDistribution[groupName] || 0) + 1;
    });

    // Get recent items (sort by _id which contains timestamp in MongoDB ObjectId)
    const recentApis = apis
      .sort((a, b) => b._id.localeCompare(a._id))
      .slice(0, 5);
    const recentDocs = docs
      .sort((a, b) => b._id.localeCompare(a._id))
      .slice(0, 3);
    const recentGroups = groups
      .sort((a, b) => b._id.localeCompare(a._id))
      .slice(0, 3);

    setStats({
      totalApis: apis.length,
      totalGroups: groups.length,
      totalDocs: docs.length,
      totalRecipes: recipes.length,
      totalIssues: issuesStats.total || 0,
      openIssues: issuesStats.openIssues || 0,
      healthIssues,
      healthIssueApis,
      groupDistribution,
      recentApis,
      recentDocs,
      recentGroups
    });
  } catch (error) {
    console.error('Error fetching stats:', error);
  } finally {
    setIsLoadingStats(false);
  }
};


  useEffect(() => {
    fetchStats();
  }, []);

  // Navigation handler
  const handleNavigate = (section, options = {}) => {
    // Cancel any active API forms when navigating to a different section
    if (section !== 'apis' || !options.keepForm) {
      setIsAddingApi(false);
      setIsEditingApi(false);
      setEditingApiData(null);
    }
    
    setActiveSection(section);
    if (options.action === 'add' && section === 'apis') {
      setIsAddingApi(true);
    }
  };

  // API handlers
  const handleAddApi = () => setIsAddingApi(true);
  const handleEditApi = async (apiData) => {
    try {
      // Fetch full details including longDescription
      const response = await apiClient.get(`${API_BASE_URL}/config/${apiData._id}`);
      setEditingApiData(response.data);
      setIsEditingApi(true);
    } catch (error) {
      message.error('Failed to fetch API details');
    }
  };
  
  const handleApiSubmit = async (values) => {
    try {
      if (isEditingApi) {
        await apiClient.put(`${API_BASE_URL}/config/${editingApiData._id}`, values);
        message.success('API updated successfully');
      } else {
        await apiClient.post(`${API_BASE_URL}/config`, values);
        message.success('API added successfully');
      }
      setIsAddingApi(false);
      setIsEditingApi(false);
      setEditingApiData(null);
      
      // Trigger refresh for all components
      setRefreshTrigger(prev => prev + 1);
      fetchStats();
      
      // Notify parent component (App.js) to refresh its API groups
      if (onApiChange) {
        onApiChange();
      }
    } catch (error) {
      message.error('Failed to save API');
    }
  };

  const menuItems = [
    {
      key: 'overview',
      icon: <DashboardOutlined />,
      label: 'Overview',
    },
    {
      key: 'apis',
      icon: <ApiOutlined />,
      label: 'APIs',
      badge: stats.totalApis
    },
    {
      key: 'groups',
      icon: <AppstoreOutlined />,
      label: 'Groups',
      badge: stats.totalGroups
    },
    {
      key: 'docs',
      icon: <FileTextOutlined />,
      label: 'Documentation',
      badge: stats.totalDocs
    },
    {
      key: 'recipes',
      icon: <BookOutlined />,
      label: 'Recipes',
      badge: stats.totalRecipes
    }

    ,
    {
      key: 'issues',
      icon: <BugOutlined />,
      label: 'Issues',
      badge: stats.openIssues,
      badgeStyle: stats.openIssues > 0 ? { backgroundColor: '#ff4d4f' } : {} // Red badge for open issues
    },
    {
      key: 'publishing',
      icon: <CloudUploadOutlined />,
      label: 'Publishing',
      style: { 
        borderTop: `1px solid ${theme.border}`, 
        marginTop: '8px',
        paddingTop: '8px'
      }
    }
  ];

  const renderContent = () => {
    // Show forms if adding/editing
    if (isAddingApi || isEditingApi) {
      return (
        <ApiConfigForm
          initialValues={editingApiData}
          isEditMode={isEditingApi}
          onSubmit={handleApiSubmit}
          onCancel={() => {
            setIsAddingApi(false);
            setIsEditingApi(false);
            setEditingApiData(null);
          }}
        />
      );
    }

    // Show section content
    switch (activeSection) {
      case 'overview':
        return <OverviewDashboard stats={stats} onNavigate={handleNavigate} onEditApi={handleEditApi} isLoadingStats={isLoadingStats} />;
      case 'apis':
        return <ApiList onAddApi={handleAddApi} onEditApi={handleEditApi} refreshTrigger={refreshTrigger} />;
      case 'groups':
        return <ApiGroupsSection refreshTrigger={refreshTrigger} />;
      case 'docs':
        return <DocumentationSection />;
      case 'recipes':
        return <RecipesSection />;
        case 'issues':
          return <IssuesSection refreshTrigger={refreshTrigger} />;
      case 'publishing':
        return <PublishingSection />;
      default:
        return <OverviewDashboard stats={stats} onNavigate={handleNavigate} />;
    }
  };

  return (
    <Layout style={{ height: '100vh', backgroundColor: theme.background }}>
      <Sider
        collapsible
        collapsed={collapsed}
        onCollapse={setCollapsed}
        width={240}
        style={{
          background: theme.surface,
          borderRight: `1px solid ${theme.border}`,
          boxShadow: currentTheme === 'dark' 
            ? '2px 0 8px rgba(0,0,0,0.3)' 
            : '2px 0 8px rgba(0,0,0,0.08)'
        }}
        theme={currentTheme === 'dark' ? 'dark' : 'light'}
      >
        <div style={{
          padding: collapsed ? '16px 8px' : '16px',
          borderBottom: `1px solid ${theme.border}`,
          textAlign: 'center'
        }}>
          <Title level={4} style={{ 
            margin: 0, 
            color: theme.primary,
            fontSize: collapsed ? '14px' : '18px',
            transition: 'all 0.3s'
          }}>
            {collapsed ? <RocketOutlined style={{ fontSize: '20px' }} /> : 'Dev Portal'}
          </Title>
        </div>
        <Menu
          mode="inline"
          selectedKeys={[activeSection]}
          onClick={({ key }) => handleNavigate(key)}
          style={{ 
            borderRight: 0, 
            background: 'transparent',
            marginTop: '8px'
          }}
          theme={currentTheme === 'dark' ? 'dark' : 'light'}
          items={menuItems.map(item => ({
            ...item,
            label: item.badge ? (
              <span style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span>{item.label}</span>
                <Badge 
                  count={item.badge} 
                  style={item.badgeStyle || { backgroundColor: theme.primary }}
                  showZero={item.key === 'issues'} // Always show issues count
                />
              </span>
            ) : item.label
          }))}
          
        />
      </Sider>
      <Layout>
        <Header style={{
          background: theme.surface,
          padding: '0 24px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          borderBottom: `1px solid ${theme.border}`,
          height: '56px'
        }}>
          <Title level={3} style={{ margin: 0, color: theme.text }}>
          {activeSection === 'overview' ? 'Overview' :
     activeSection === 'apis' ? 'APIs' :
     activeSection === 'groups' ? 'Groups' :
     activeSection === 'docs' ? 'Documentation' :
     activeSection === 'recipes' ? 'Recipes' :
     activeSection === 'issues' ? 'Issues' : 
     activeSection === 'publishing' ? 'Publishing' : 'API Manager'}
             
          </Title>
          <Button onClick={onCancel}>
            Back to Sandbox
          </Button>
        </Header>
        <Content style={{
          margin: 0,
          background: theme.background,
          overflow: 'auto'
        }}>
          {renderContent()}
        </Content>
      </Layout>
    </Layout>
  );
};

export default ApiManager;